import { initializeApollo } from '../lib/apollo-client';
import requestHandler from '../requestHandler';
import type {
  CompleteLearnMutation,
  CompleteLearnMutationVariables,
  CompleteLessonMutation,
  CompleteLessonMutationVariables,
  CreateLearnMutation,
  CreateLearnMutationVariables,
  FetchCourseLearnQuery,
  FetchCourseLearnQueryVariables,
  FetchLearnQuery,
  FetchLearnQueryVariables,
  LearnPage,
  LearnPageQuery,
} from '@edwin-group/people-graphql';
import { getAuthentication } from '@/hooks/useAuthentication';
import {
  MUTATION_COMPLETE_LEARN,
  MUTATION_COMPLETE_LESSON,
  MUTATION_CREATE_LEARN,
  QUERY_LEARN_PAGE,
  FETCH_COURSE_LEARN,
  FETCH_LEARN,
} from '@edwin-group/people-graphql';

export const fetchLearn = async ({ completed }: { completed?: boolean }) => {
  const apolloClient = initializeApollo();
  const { user } = getAuthentication();

  if (!user) {
    return null;
  }

  const { data, error } = await requestHandler(
    apolloClient.query<FetchLearnQuery, FetchLearnQueryVariables>({
      query: FETCH_LEARN,
      variables: {
        staffId: user.documentId || '',
        completed,
      },
    }),
  );

  if (error) {
    console.error(`Error fetching user ${user.id} learn`, error);
    return null;
  }

  if (!data?.learn?.data.length) {
    return null;
  }

  return data?.learn;
};

export const fetchCourseLearn = async (courseSlug: string) => {
  const apolloClient = initializeApollo();
  const { user } = getAuthentication();

  if (!user) {
    return null;
  }

  const { data, error } = await requestHandler(
    apolloClient.query<FetchCourseLearnQuery, FetchCourseLearnQueryVariables>({
      query: FETCH_COURSE_LEARN,
      fetchPolicy: 'no-cache',
      variables: {
        staffId: user.documentId || '',
        courseSlug,
      },
    }),
  );

  if (error) {
    console.error(`Error fetching user ${user.id} learn ${courseSlug}`, error);
    return null;
  }

  if (!data?.learn?.data.length) {
    return null;
  }

  return data?.learn?.data[0];
};

export async function completeLesson(idLesson: string, idModule: string, idLearn: string) {
  const apolloClient = initializeApollo();

  return requestHandler(
    apolloClient.mutate<CompleteLessonMutation, CompleteLessonMutationVariables>({
      mutation: MUTATION_COMPLETE_LESSON,
      fetchPolicy: 'no-cache',
      variables: {
        idLesson,
        idModule,
        idLearn,
      },
    }),
  );
}

export async function createLearn(idCourse: string) {
  const apolloClient = initializeApollo();

  return requestHandler(
    apolloClient.mutate<CreateLearnMutation, CreateLearnMutationVariables>({
      mutation: MUTATION_CREATE_LEARN,
      fetchPolicy: 'no-cache',
      variables: {
        staffId: getAuthentication()?.user?.documentId || '',
        idCourse,
      },
    }),
  );
}

export async function completeLearn(idLearn: string) {
  const apolloClient = initializeApollo();

  return requestHandler(
    apolloClient.mutate<CompleteLearnMutation, CompleteLearnMutationVariables>({
      mutation: MUTATION_COMPLETE_LEARN,
      fetchPolicy: 'no-cache',
      variables: {
        idLearn,
      },
    }),
  );
}

export const getPage = async () => {
  const apolloClient = initializeApollo();

  const { data, error } = await requestHandler(
    apolloClient.query<LearnPageQuery>({
      query: QUERY_LEARN_PAGE,
      fetchPolicy: 'cache-first',
    }),
  );

  return {
    page: data?.learnPage?.data?.attributes as LearnPage,
    error,
  };
};
